'use client';

import { c as _c } from "react/compiler-runtime";
import type { ResearchFile } from '@shared/all/domains/socket';
import { researchFileMeta } from '@shared/all/helpers/researchFile';
import useResearchFile, { RESEARCH_FILE_CHOICES } from '@shared/utils/hooks/useResearchFile';
import { Download, FileText, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import DocumentModal from '../onedrive/DocumentModal';

// A file the assistant made into the user's vault, under the answer that made
// it: open it, download it, or — when a file already sat at that path — say
// whether it may replace it. The assistant never replaces one on its own.
// The factiii.com twin of apps/oakbox-web's AskFileCard: an attachment, not a
// form, so the card opens the file and Download is an icon.

const ResearchFileCard = t0 => {
  const $ = _c(40);
  const {
    file: initial,
    sessionId
  } = t0;
  const {
    file,
    busy,
    name,
    saved,
    downloadUrl,
    resolve
  } = useResearchFile(client, initial, sessionId);
  const [reading, setReading] = useState(false);
  let t1;
  if ($[0] !== downloadUrl) {
    t1 = async () => {
      const url = await downloadUrl();
      if (url) {
        window.location.assign(url);
      }
    };
    $[0] = downloadUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const download = t1;
  let t2;
  if ($[2] !== file.status) {
    t2 = <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-ds-border-standard text-primary">{file.status === "saving" ? <Loader2 className="size-4 animate-spin" /> : <FileText className="size-4" />}</span>;
    $[2] = file.status;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== name) {
    t3 = <span className="block truncate text-sm font-emphasis text-textPrimary group-hover:underline">{name}</span>;
    $[4] = name;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = `block text-xs ${file.status === "failed" ? "text-red-500" : "text-textSecondary"}`;
  let t5;
  if ($[6] !== file) {
    t5 = researchFileMeta(file);
    $[6] = file;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t6) {
    t7 = <span className="min-w-0 text-left">{t3}{t6}</span>;
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t2 || $[15] !== t7) {
    t8 = <>{t2}{t7}</>;
    $[14] = t2;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const body = t8;
  let t9;
  if ($[17] !== body || $[18] !== saved) {
    t9 = saved ? <button type="button" className="group flex min-w-0 flex-1 items-center gap-3 p-2" onClick={() => setReading(true)}>{body}</button> : <div className="flex min-w-0 flex-1 items-center gap-3 p-2">{body}</div>;
    $[17] = body;
    $[18] = saved;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== busy || $[21] !== download || $[22] !== name || $[23] !== saved) {
    t10 = saved && <button type="button" aria-label={`Download ${name}`} title="Download" disabled={busy === "download"} onClick={() => void download()} className="flex size-8 shrink-0 items-center justify-center rounded-lg text-textSecondary hover:bg-hover hover:text-textPrimary disabled:opacity-60">{busy === "download" ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}</button>;
    $[20] = busy;
    $[21] = download;
    $[22] = name;
    $[23] = saved;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = <div className="flex items-center pr-1.5">{t9}{t10}</div>;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== busy || $[29] !== file.status || $[30] !== resolve) {
    t12 = file.status === "confirm" && <div className="flex flex-wrap gap-1.5 px-2 pb-2">{RESEARCH_FILE_CHOICES.map(t13 => {
        const {
          choice,
          label,
          bad
        } = t13;
        return <button key={choice} type="button" disabled={busy !== null} onClick={() => void resolve(choice)} className={`flex h-7 items-center rounded-lg border border-ds-border-standard bg-background-primary px-2.5 text-xs font-semibold hover:border-textSecondary disabled:opacity-60 ${bad ? "text-red-500" : "text-textPrimary"}`}>{busy === choice ? <Loader2 className="size-3 animate-spin" /> : label}</button>;
      })}</div>;
    $[28] = busy;
    $[29] = file.status;
    $[30] = resolve;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== file.uploadId || $[33] !== name || $[34] !== reading) {
    t13 = reading && file.uploadId && <DocumentModal target={{
      uploadId: file.uploadId,
      name
    }} closeHandler={() => setReading(false)} />;
    $[32] = file.uploadId;
    $[33] = name;
    $[34] = reading;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t11 || $[37] !== t12 || $[38] !== t13) {
    t14 = <div className="mt-1 max-w-sm rounded-xl border border-ds-border-standard bg-background-light">{t11}{t12}{t13}</div>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  return t14;
};
export default ResearchFileCard;